import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { CloseButton } from '../CloseButton';
import { forwardRef } from 'react';
import { Stack } from '../Stack';
import { contentShowKeyframes, contentShowMobileKeyframes, overlayShowKeyframes } from '../../keyframes';
import { preventDefault } from '../../lib/utils.lib';
import { styled } from '../../system';
import { wrapWithDefaultProps } from '../../lib/wrap-with-default-props';
export const StyledOverlay = styled(DialogPrimitive.Overlay, {
    position: 'fixed',
    inset: 0,
    bgColor: '$backdrop',
    backdropFilter: 'blur(5px)',
    zIndex: '$30',
    '@media (prefers-reduced-motion: no-preference)': {
        willChange: 'opacity',
        animation: `${overlayShowKeyframes} 150ms cubic-bezier(0.16, 1, 0.3, 1)`
    },
    variants: {
        invisible: {
            true: {
                left: 0,
                top: 0,
                minMaxSize: 0
            }
        }
    }
});
export const StyledContent = styled(DialogPrimitive.Content, {
    p: '$6',
    bgColor: '$surface',
    rounded: '$2xl',
    shadow: '$xl',
    position: 'fixed',
    top: '50%',
    bottom: 'auto',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    w: '90vw',
    maxW: '450px',
    maxH: '85vh',
    zIndex: '$40',
    '@media (prefers-reduced-motion: no-preference)': {
        willChange: 'transform, opacity',
        animation: `${contentShowKeyframes} 150ms cubic-bezier(0.16, 1, 0.3, 1)`
    },
    '&:focus': { outline: 'none' },
    '@xs': {
        top: 'auto',
        bottom: '10%',
        transform: 'translate(-50%, 0)',
        '@media (prefers-reduced-motion: no-preference)': {
            willChange: 'transform, opacity',
            animation: `${contentShowMobileKeyframes} 150ms cubic-bezier(0.16, 1, 0.3, 1)`
        }
    }
});
export const StyledTitle = styled(DialogPrimitive.Title, {
    textLg: true,
    fontWeight: '$medium',
    m: 0
});
export const StyledDescription = styled(DialogPrimitive.Description, {
    textSm: true,
    color: '$textMuted',
    m: 0,
    mt: '$1'
});
export const StyledCloseButton = styled(CloseButton, {
    position: 'absolute',
    top: '$4',
    right: '$4'
});
export const ModalContent = wrapWithDefaultProps(
// eslint-disable-next-line react/display-name
forwardRef(({ children, noBackdrop, ...props }, ref) => (_jsxs(DialogPrimitive.Portal, { children: [_jsx(StyledOverlay, { invisible: noBackdrop }), _jsx(StyledContent, { ref: ref, ...props, children: children })] }))), {
    noBackdrop: false,
    onOpenAutoFocus: preventDefault
});
export const Modal = styled(DialogPrimitive.Root, {});
export const ModalTrigger = wrapWithDefaultProps(styled(DialogPrimitive.Trigger, {}), {
    asChild: true
});
export const ModalTitle = StyledTitle;
export const ModalDescription = StyledDescription;
export const ModalCloseTrigger = styled(DialogPrimitive.Close, {});
export const ModalHeader = styled('div', {
    mb: '$5'
});
export const ModalBody = styled('div', {});
export const ModalActions = wrapWithDefaultProps(styled(Stack, {
    justifyContent: 'flex-end',
    mt: '$8',
    '& > button': {
        flex: 1
    }
}), {
    gap: '$3',
    flow: {
        '@xs': 'column',
        '@sm': 'row'
    }
});
export const ModalCloseButton = forwardRef((props, ref) => (_jsx(ModalCloseTrigger, { ref: ref, asChild: true, children: _jsx(StyledCloseButton, { autoFocus: false, ...props }) })));
Modal.displayName = 'Modal';
ModalTrigger.displayName = 'ModalTrigger';
ModalTitle.displayName = 'ModalTitle';
ModalDescription.displayName = 'ModalDescription';
ModalCloseTrigger.displayName = 'ModalCloseTrigger';
ModalHeader.displayName = 'ModalHeader';
ModalBody.displayName = 'ModalBody';
ModalActions.displayName = 'ModalActions';
ModalContent.displayName = 'ModalContent';
ModalCloseButton.displayName = 'ModalCloseButton';
